/**
 * PortfolioInsightsStrip — Astor-authored portfolio insights card row.
 *
 * Surfaces 0-4 cards on the borrower dashboard composed from the borrower's
 * loan portfolio (case stage, document aging, rate posture, DSCR coverage).
 * Wired to `trpc.astor.getPortfolioInsights` — deterministic projection in
 * `8-agent-service` today, with a stable contract that lets a future LLM
 * swap stay compatible.
 *
 * Empty result = hidden strip entirely (no chrome). Loading state matches
 * the placeholder grid height so the dashboard doesn't reflow.
 */

import CautionIcon from '@mui/icons-material/InfoOutlined';
import NeutralIcon from '@mui/icons-material/Lightbulb';
import MintIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  baseColors,
  GlassButton,
  Grid,
  iconSizes,
  type SvgIconProps,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { AstorInsightCardDto, AstorInsightTone } from '@ops-matrix/shared-types';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAstorPortfolioInsights } from '../../lib/trpc/hooks';
import { AstorAvatar } from '../loans/AstorAvatar';

const ICON_BY_TONE: Record<AstorInsightTone, ComponentType<SvgIconProps>> = {
  MINT: MintIcon,
  AMBER: CautionIcon,
  NEUTRAL: NeutralIcon,
};

function toneToColor(tone: AstorInsightTone): string {
  switch (tone) {
    case 'MINT':
      return baseColors.teal[400];
    case 'AMBER':
      return baseColors.amber[400];
    case 'NEUTRAL':
    default:
      return baseColors.info[400];
  }
}

export function PortfolioInsightsStrip(): React.ReactElement | null {
  const { t } = useI18n();
  const navigate = useNavigate();
  const insightsQuery = useAstorPortfolioInsights();

  const cards = insightsQuery.data?.cards ?? [];

  // Hide the strip entirely when there's nothing notable. The dashboard
  // composer is expected to handle the missing surface gracefully; we
  // do NOT render a placeholder grid so the borrower never sees "empty
  // chrome" — Astor only speaks when it has something to say.
  if (!insightsQuery.isLoading && cards.length === 0) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AstorAvatar size={18} />
          <Typography variant="h6">{t('consumer.home.insights.title')}</Typography>
        </Box>
        <GlassButton size="small" variant="text" onClick={() => navigate('/chat')}>
          {t('consumer.home.insights.viewAll')}
        </GlassButton>
      </Box>

      <Grid container spacing={1.5}>
        {cards.map((card: AstorInsightCardDto) => {
          const toneColor = toneToColor(card.tone);
          const Icon = ICON_BY_TONE[card.tone];
          return (
            <Grid key={card.id} size={{ xs: 12, md: 4 }}>
              <Box
                role={card.href ? 'link' : undefined}
                onClick={card.href ? () => navigate(card.href as string) : undefined}
                sx={{
                  position: 'relative',
                  p: 3,
                  pt: 3.5,
                  height: '100%',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  overflow: 'hidden',
                  cursor: card.href ? 'pointer' : 'default',
                  '&:hover': card.href ? { borderColor: toneColor, bgcolor: 'action.hover' } : undefined,
                  '&:focus-visible': card.href ? { outline: `2px solid ${toneColor}`, outlineOffset: 2 } : undefined,
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: toneColor,
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Icon ? <Icon sx={{ fontSize: iconSizes.base, color: toneColor }} /> : null}
                  {card.metricLabel ? (
                    <Typography variant="overline" sx={{ color: toneColor, letterSpacing: '0.08em' }}>
                      {card.metricLabel}
                    </Typography>
                  ) : null}
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {card.headline}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
                  {card.body}
                </Typography>
                {card.metric ? (
                  <Typography variant="body1" sx={{ fontWeight: 700, color: toneColor }}>
                    {card.metric}
                  </Typography>
                ) : null}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
